<template>
  <div class="sig-upload">
    <div class="section-head">
      <span class="section-label">Hình ảnh chữ ký</span>
      <span class="section-hint">Tùy chọn</span>
    </div>

    <!-- Loại chữ ký -->
    <div class="type-selector">
      <button
        v-for="t in signTypes"
        :key="t.key"
        class="type-btn"
        :class="{ active: selectedType === t.key }"
        @click="selectType(t.key)"
      >
        {{ t.label }}
        <span class="type-size">{{ t.width }}x{{ t.height }}</span>
      </button>
    </div>

    <input ref="fileInput" type="file" accept="image/png,image/jpeg" class="sr-only" @change="onFileSelected">

    <!-- Preview when image exists -->
    <div v-if="value && !showCropper" class="preview-row">
      <div class="preview-frame">
        <img :src="'data:image/png;base64,' + value" alt="Chữ ký">
      </div>
      <div class="preview-actions">
        <button class="sm-btn" @click="$refs.fileInput.click()">Đổi</button>
        <button class="sm-btn" @click="recrop">Cắt lại</button>
        <button class="sm-btn sm-btn--red" @click="clearImage">Xóa</button>
      </div>
    </div>

    <!-- Upload button when no image -->
    <button v-if="!value && !showCropper" class="upload-btn" @click="$refs.fileInput.click()">
      <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/></svg>
      Chọn ảnh chữ ký (PNG/JPG)
    </button>

    <!-- Cropper Modal -->
    <div v-if="showCropper" class="cropper-overlay" @click.self="cancelCrop">
      <div class="cropper-dialog">
        <div class="cropper-header">
          <div>
            <h3>Cắt ảnh chữ ký</h3>
            <p>Kéo để chọn vùng cần cắt &mdash; {{ currentTypeDef.label }} ({{ currentTypeDef.width }}x{{ currentTypeDef.height }} pt)</p>
          </div>
          <button class="cropper-close" @click="cancelCrop">&times;</button>
        </div>

        <!-- Type selector inside modal -->
        <div class="cropper-types">
          <button
            v-for="t in signTypes"
            :key="t.key"
            class="ctype-btn"
            :class="{ active: selectedType === t.key }"
            @click="selectType(t.key)"
          >
            <span class="ctype-icon" :class="'ctype-icon--' + t.key"></span>
            {{ t.label }}
            <span class="ctype-dim">{{ t.width }}x{{ t.height }}</span>
          </button>
        </div>

        <div class="cropper-body" ref="cropperContainer">
          <img ref="cropperImage" :src="rawImageDataUrl" class="cropper-source">
        </div>

        <div class="cropper-footer">
          <button class="cr-btn cr-btn--cancel" @click="cancelCrop">Hủy</button>
          <button class="cr-btn cr-btn--ok" @click="applyCrop">
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/></svg>
            Xác nhận cắt ảnh
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.min.css'

var SIGN_TYPES = [
  { key: 'signature', label: 'Chữ ký', width: 200, height: 80, ratio: 200 / 80 },
  { key: 'stamp', label: 'Mộc', width: 150, height: 150, ratio: 1 },
  { key: 'stamp-rect', label: 'Mộc chữ nhật', width: 200, height: 60, ratio: 200 / 60 },
  { key: 'custom', label: 'Tự chọn', width: 200, height: 80, ratio: NaN }
]

export default {
  name: 'SignatureUploader',
  props: { value: { type: String, default: null } },

  data: function () {
    return {
      selectedType: 'signature',
      showCropper: false,
      rawImageDataUrl: null,
      cropper: null
    }
  },

  computed: {
    signTypes: function () { return SIGN_TYPES },
    currentTypeDef: function () {
      var self = this
      return SIGN_TYPES.find(function (t) { return t.key === self.selectedType }) || SIGN_TYPES[0]
    }
  },

  methods: {
    selectType: function (key) {
      this.selectedType = key
      this.$emit('type-change', this.currentTypeDef)
      if (this.cropper) {
        var def = SIGN_TYPES.find(function (t) { return t.key === key })
        if (def && !isNaN(def.ratio)) {
          this.cropper.setAspectRatio(def.ratio)
        } else {
          this.cropper.setAspectRatio(NaN)
        }
      }
    },

    onFileSelected: function (event) {
      var file = event.target.files[0]
      if (!file) return
      if (!file.type.match(/^image\/(png|jpeg)$/)) {
        this.$emit('log', '[Anh] LOI: Chỉ hỗ trợ PNG hoặc JPG')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$emit('log', '[Anh] LOI: File quá lớn (tối đa 5MB)')
        return
      }
      var self = this
      var reader = new FileReader()
      reader.onload = function (e) {
        self.rawImageDataUrl = e.target.result
        self.showCropper = true
        self.$emit('log', '[Anh] Đang mở trình cắt ảnh: ' + file.name)
        self.$nextTick(function () { self.initCropper() })
      }
      reader.readAsDataURL(file)
    },

    initCropper: function () {
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
      var overlay = this.$el.querySelector('.cropper-overlay')
      if (overlay && overlay.parentNode !== document.body) {
        document.body.appendChild(overlay)
      }
      var img = this.$refs.cropperImage
      if (!img) return

      var def = this.currentTypeDef
      var aspectRatio = isNaN(def.ratio) ? NaN : def.ratio

      this.cropper = new Cropper(img, {
        aspectRatio: aspectRatio,
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 0.8,
        restore: false,
        guides: true,
        center: true,
        highlight: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        background: true
      })
    },

    applyCrop: function () {
      if (!this.cropper) return

      var canvas = this.cropper.getCroppedCanvas({
        maxWidth: 800,
        maxHeight: 800,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      })

      var dataUrl = canvas.toDataURL('image/png')
      var base64 = dataUrl.split(',')[1]

      this.$emit('input', base64)
      this.$emit('type-change', this.currentTypeDef)
      this.$emit('log', '[Anh] OK - Đã cắt ảnh (' + this.currentTypeDef.label + ': ' + this.currentTypeDef.width + 'x' + this.currentTypeDef.height + ')')

      this.closeCropper()
    },

    cancelCrop: function () {
      this.closeCropper()
    },

    recrop: function () {
      if (this.value) {
        this.rawImageDataUrl = 'data:image/png;base64,' + this.value
        this.showCropper = true
        var self = this
        this.$nextTick(function () { self.initCropper() })
      }
    },

    closeCropper: function () {
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
      var overlay = document.body.querySelector('.cropper-overlay')
      if (overlay && this.$el) {
        this.$el.appendChild(overlay)
      }
      this.showCropper = false
      this.rawImageDataUrl = null
    },

    clearImage: function () {
      this.$refs.fileInput.value = ''
      this.$emit('input', null)
      this.$emit('log', '[Anh] Đã xóa ảnh chữ ký')
      this.closeCropper()
    }
  },

  beforeDestroy: function () {
    if (this.cropper) {
      this.cropper.destroy()
    }
  }
}
</script>

<style scoped>
.sr-only { position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0); }
.section-head { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
.section-label { font-size:13px; font-weight:600; color:#1e293b; }
.section-hint { font-size:11px; color:#94a3b8; font-style:italic; }

/* Type selector */
.type-selector {
  display:flex; gap:4px; margin-bottom:10px; flex-wrap:wrap;
}
.type-btn {
  padding:5px 10px; border:1px solid #e2e8f0; border-radius:6px;
  background:#fff; color:#475569; font-size:11px; font-family:inherit;
  cursor:pointer; display:flex; flex-direction:column; align-items:center; gap:1px;
  transition:all 0.15s;
}
.type-btn:hover { border-color:#7abee0; background:#f0f7fb; }
.type-btn.active {
  border-color:#00568c; background:#f0f7fb; color:#003d63;
  box-shadow: 0 0 0 1px #00568c;
}
.type-size { font-size:9px; color:#94a3b8; font-family:'JetBrains Mono',monospace; }
.type-btn.active .type-size { color:#3d9fd4; }

/* Upload button */
.upload-btn {
  width:100%; padding:10px; border:1px dashed #cbd5e1; border-radius:6px;
  background:#f8fafc; color:#64748b; font-size:12px; font-family:inherit;
  cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px;
  transition:all 0.2s;
}
.upload-btn:hover { border-color:#7abee0; background:#f0f7fb; color:#00568c; }
.upload-btn svg { width:16px; height:16px; }

/* Preview */
.preview-row { display:flex; align-items:center; gap:10px; }
.preview-frame {
  flex:1; padding:6px; background:#fafafa;
  border:1px solid #e2e8f0; border-radius:6px;
  display:flex; align-items:center; justify-content:center;
  background-image: linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
    linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
    linear-gradient(-45deg, transparent 75%, #f0f0f0 75%);
  background-size: 10px 10px;
  background-position: 0 0, 0 5px, 5px -5px, -5px 0px;
}
.preview-frame img { max-width:100%; max-height:60px; object-fit:contain; }
.preview-actions { display:flex; flex-direction:column; gap:3px; }
.sm-btn {
  padding:3px 8px; border:1px solid #e2e8f0; border-radius:4px;
  background:#fff; color:#475569; font-size:10px; font-family:inherit; cursor:pointer;
  white-space:nowrap;
}
.sm-btn:hover { background:#f1f5f9; }
.sm-btn--red { color:#dc2626; border-color:#fecaca; }
.sm-btn--red:hover { background:#fef2f2; }

/* Cropper Modal Overlay */
.cropper-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 999999;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: cropFadeIn 0.2s ease;
}
@keyframes cropFadeIn { from { opacity: 0; } to { opacity: 1; } }

.cropper-dialog {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
  animation: cropSlideUp 0.25s ease;
}
@keyframes cropSlideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

.cropper-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.cropper-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}
.cropper-header p {
  font-size: 12px;
  color: #94a3b8;
  margin: 4px 0 0;
}
.cropper-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.cropper-close:hover { background: #fee2e2; color: #dc2626; }

/* Type selector in modal */
.cropper-types {
  display: flex;
  gap: 6px;
  padding: 12px 20px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
}
.ctype-btn {
  padding: 6px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}
.ctype-btn:hover { border-color: #7abee0; background: #f0f7fb; }
.ctype-btn.active {
  border-color: #00568c;
  background: #f0f7fb;
  color: #003d63;
  box-shadow: 0 0 0 1px #00568c;
}
.ctype-dim {
  font-size: 10px;
  color: #94a3b8;
  font-family: 'JetBrains Mono', monospace;
}
.ctype-btn.active .ctype-dim { color: #3d9fd4; }
.ctype-icon {
  width: 16px;
  height: 12px;
  border: 1.5px solid currentColor;
  border-radius: 2px;
  display: inline-block;
}
.ctype-icon--stamp { width: 14px; height: 14px; border-radius: 50%; }
.ctype-icon--stamp-rect { width: 18px; height: 8px; }
.ctype-icon--custom { border-style: dashed; }

/* Cropper area */
.cropper-body {
  flex: 1;
  min-height: 300px;
  max-height: 50vh;
  overflow: hidden;
  background: #e9ecef;
}
.cropper-source {
  display: block;
  max-width: 100%;
}

/* Footer */
.cropper-footer {
  padding: 14px 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  background: #fafbfc;
}
.cr-btn {
  padding: 8px 20px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}
.cr-btn--cancel {
  background: #f1f5f9;
  color: #475569;
}
.cr-btn--cancel:hover { background: #e2e8f0; }
.cr-btn--ok {
  background: #00568c;
  color: #fff;
}
.cr-btn--ok:hover { background: #003d63; box-shadow: 0 2px 8px rgba(0,86,140,0.3); }

@media (max-width: 768px) {
  .cropper-overlay { padding: 8px; }
  .cropper-dialog { max-width: 100%; border-radius: 10px; }
  .cropper-types { flex-wrap: wrap; }
}
</style>
